import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './Modal.css';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  maxWidth?: number;
  className?: string;
  /** Id del elemento que titula el diálogo, emitido como aria-labelledby. */
  labelledBy?: string;
}

export function Modal({
  open,
  onClose,
  children,
  maxWidth = 640,
  className = '',
  labelledBy,
}: ModalProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // El foco entra al diálogo al abrir y regresa al disparador al cerrar, para que
  // el teclado no quede navegando el fondo mientras el modal está visible.
  useEffect(() => {
    if (!open) return;
    triggerRef.current = document.activeElement as HTMLElement | null;
    boxRef.current?.focus();
    return () => triggerRef.current?.focus();
  }, [open]);

  if (!open) return null;

  // DEUDA TÉCNICA PAGADA (Agosto 2026-08-19) — antes este overlay se montaba en el punto del árbol
  // donde vive el consumidor. `position: fixed` (`.modal-overlay`, global.css) solo se ancla al
  // VIEWPORT si NINGÚN ancestro tiene `transform`/`filter`/`perspective`/`will-change` propios (esas
  // propiedades crean su propio "containing block"); `.view` (ancestro de toda pantalla de Pulso)
  // anima con `transform: translateY()` (`@keyframes fadeUp`), así que el overlay quedaba anclado a
  // la CAJA de `.view` — que mide lo que mide el contenido, no la pantalla — en vez del viewport. Con
  // poco contenido esa caja era más baja que el modal y este se abría cortado por arriba (bug
  // original: AdminDocumentacion con la pestaña sin datos, ver recognition.css:5792 antes de este
  // fix). `createPortal` a `document.body` es el mismo patrón que ya usan `CampaignDocs`,
  // `OuTreeSelect` y `RecognitionDetailOverlay`: monta el overlay como hijo directo de `<body>`,
  // fuera de CUALQUIER ancestro con transform, sin tocar una sola clase CSS (mismas reglas de
  // siempre, incluida la variante de pantalla completa en móvil). Afecta a las ~11 pantallas que
  // usan este componente compartido (AdminDocumentacion, AdminWinners, AdminModeration,
  // AdminRoleEditorModal, AdminCampaignBadgeEditorModal, AdminBadgeEditorModal, BadgeCelebration,
  // GestionAdministradores, RolesPulsoEditorModal, DailyHabitsModal, accessShared) de una sola vez.
  return createPortal(
    <div className="modal-overlay modal-overlay--fade funcat-modal-overlay" onClick={onClose}>
      <div
        ref={boxRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className={`funcat-modal-box ${className}`}
        style={{ maxWidth }}
        onClick={event => event.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

interface ModalHeaderProps {
  children: ReactNode;
  onClose: () => void;
}

export function ModalHeader({ children, onClose }: ModalHeaderProps) {
  const { t } = useTranslation();
  return (
    <div className="funcat-modal-header">
      <div className="funcat-modal-title">{children}</div>
      <button
        className="btn btn--ghost btn--sm"
        onClick={onClose}
        aria-label={t('common.close')}
      >
        <i className="fas fa-times" aria-hidden="true" />
      </button>
    </div>
  );
}

export function ModalBody({ children }: { children: ReactNode }) {
  return <div className="funcat-modal-body">{children}</div>;
}

export function ModalFooter({ children }: { children: ReactNode }) {
  return <div className="funcat-modal-footer">{children}</div>;
}
